import {
  AssociateSoftwareTokenCommand,
  AuthFlowType,
  ChallengeNameType,
  CognitoIdentityProviderClient,
  ConfirmForgotPasswordCommand,
  ForgotPasswordCommand,
  GetUserCommand,
  GlobalSignOutCommand,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  SetUserMFAPreferenceCommand,
  SMSMfaSettingsType,
  SoftwareTokenMfaSettingsType,
  VerifySoftwareTokenCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import {
  createSrpSession,
  signSrpSession,
  wrapAuthChallenge,
  wrapInitiateAuth,
} from 'cognito-srp-helper';
import {
  AuthResponseDTO,
  CredentialsDTO,
  MFASelectionType,
  MFASetupDTO,
  ResetPasswordConfirmDTO,
  ResetPasswordRequestDTO,
  UserInfoDTO,
  RegisterUserDTO,
} from '../dtos';
import { IAuthService } from './iAuthService';
import type {
  CognitoConfig,
  AuthSession,
  TokenPayload,
  CurrentChallenge,
  CognitoError,
} from './types';

/**
 * SECURITY NOTE: This service stores JWT tokens in localStorage which is vulnerable to XSS attacks.
 * Future improvements should consider:
 * 1. HTTP-only cookies for refresh tokens
 * 2. Secure token storage in Web Crypto API
 * 3. Token encryption for sensitive data
 * 4. Content Security Policy (CSP) implementation
 * 5. Regular security audits and penetration testing
 */
export class CognitoAuthService implements IAuthService {
  private cognitoClient: CognitoIdentityProviderClient;
  private config: CognitoConfig | null = null;
  private currentSession: AuthSession | null = null;
  private currentChallenge: CurrentChallenge | null = null;
  private tokenUpdateCallbacks: Array<
    (tokens: {
      accessToken: string | null;
      idToken: string | null;
      refreshToken: string | null;
    }) => void
  > = [];

  private static readonly SESSION_STORAGE_KEY = 'cognito_auth_session';
  private static readonly TOKEN_BUFFER_TIME = 10 * 60 * 1000; // 10 minutes buffer before expiration
  private static readonly STORAGE_ENCRYPTION_KEY = 'lecturo_auth_v1'; // Simple obfuscation key

  constructor() {
    this.cognitoClient = new CognitoIdentityProviderClient({
      region: import.meta.env.VITE_AWS_REGION || 'eu-west-1',
    });

    // DON'T automatically load session - let AuthStore handle this via restoreSession()
    // This prevents race conditions with localStorage clearing

    // Perform security checks
    this.performSecurityChecks();
  }

  configure(config: { userPoolId: string; appClientId: string }): void {
    this.config = {
      ...config,
      region: import.meta.env.VITE_AWS_REGION || 'eu-west-1',
    };

    this.cognitoClient = new CognitoIdentityProviderClient({
      region: this.config.region,
    });
  }

  private parseTokenPayload(token: string): TokenPayload {
    try {
      const payload = token.split('.')[1];
      // Convert Base64url to Base64 by adding padding and replacing URL-safe characters
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);

      // Use universal base64 decoding that works in both browser and Node.js
      let decoded: string;
      if (typeof atob !== 'undefined') {
        // Browser environment
        decoded = atob(padded);
      } else {
        // Node.js environment (e.g., SSR)
        decoded = Buffer.from(padded, 'base64').toString('utf-8');
      }

      return JSON.parse(decoded);
    } catch (error) {
      console.error('Failed to parse token payload:', error);
      return {};
    }
  }

  private getTokenExpiration(token: string): number {
    try {
      const payload = this.parseTokenPayload(token);
      return payload.exp ? payload.exp * 1000 : 0; // Convert to milliseconds
    } catch (error) {
      console.error('Failed to get token expiration:', error);
      return 0;
    }
  }

  private saveSessionToStorage(_session: AuthSession): void {
    // Session persistence is now handled by the auth store
    // This method is kept for API compatibility but delegates to store
    this.notifyTokenUpdate();
  }

  private loadSessionFromStorage(): void {
    // Session loading is now handled by the auth store during initialization
    // This method attempts to restore from auth store if available
    try {
      // Check if we can restore tokens from the auth store
      const storedTokens = this.getStoredTokensFromAuthStore();
      if (storedTokens && storedTokens.idToken && storedTokens.refreshToken) {
        // Reconstruct session from stored tokens
        const session = this.reconstructSessionFromTokens(storedTokens);
        if (session) {
          this.currentSession = session;
        }
      }
    } catch (error) {
      console.error('Failed to load session from auth store:', error);
    }
  }

  /**
   * Public method to restore session from stored tokens
   * Called by auth store during initialization
   */
  public restoreSession(tokens: {
    accessToken: string;
    idToken: string;
    refreshToken: string;
    username: string;
  }): void {
    try {
      if (tokens.idToken && tokens.refreshToken) {
        // Validate token format before reconstruction
        if (!tokens.idToken.includes('.') || !tokens.refreshToken.trim()) {
          return;
        }

        const session = this.reconstructSessionFromTokens(tokens);
        if (session) {
          this.currentSession = session;

          // Check if the restored session is already expired
          if (this.isSessionExpired(session)) {
            this.currentSession = null;
          }
        }
      }
    } catch (error) {
      console.error('Failed to restore session:', error);
      this.currentSession = null;
    }
  }

  private clearSessionFromStorage(): void {
    // Clear the in-memory session
    this.currentSession = null;

    // Notify that tokens have been cleared (AuthStore handles localStorage)
    this.notifyTokenUpdate();
  }

  private getStoredTokensFromAuthStore(): {
    idToken: string;
    refreshToken: string;
    accessToken: string;
  } | null {
    try {
      // Try to access auth store state directly
      // This is a temporary solution - ideally would be injected as dependency
      const authStoreData = localStorage.getItem('auth-store');
      if (authStoreData) {
        const parsed = JSON.parse(authStoreData);
        const state = parsed.state || parsed;
        if (state.idToken && state.refreshToken) {
          return {
            idToken: state.idToken,
            refreshToken: state.refreshToken,
            accessToken: state.accessToken || state.idToken,
          };
        }
      }
    } catch (error) {
      // Silently handle auth store access errors
    }
    return null;
  }

  private reconstructSessionFromTokens(tokens: {
    idToken: string;
    refreshToken: string;
    accessToken: string;
  }): AuthSession | null {
    try {
      const payload = this.parseTokenPayload(tokens.idToken);
      if (!payload.exp || !payload.sub) {
        return null;
      }

      return {
        accessToken: tokens.accessToken,
        idToken: tokens.idToken,
        refreshToken: tokens.refreshToken,
        username: payload.email || payload.sub,
        expiresAt: payload.exp * 1000, // Convert to milliseconds
      };
    } catch (error) {
      console.error('Failed to reconstruct session from tokens:', error);
      return null;
    }
  }

  private isSessionExpired(session: AuthSession): boolean {
    const now = Date.now();
    return session.expiresAt - CognitoAuthService.TOKEN_BUFFER_TIME <= now;
  }

  private async ensureValidSession(): Promise<string> {
    if (!this.currentSession) {
      throw new Error('No active session found');
    }

    // Check if session is expired and refresh if needed
    if (this.isSessionExpired(this.currentSession)) {
      try {
        return await this.refreshSession();
      } catch (error) {
        // If refresh fails, clear the session
        this.currentSession = null;
        this.clearSessionFromStorage();
        throw new Error('Session expired and refresh failed. Please sign in again.');
      }
    }

    return this.currentSession.idToken;
  }

  private createSessionWithExpiration(
    accessToken: string,
    idToken: string,
    refreshToken: string,
    username: string
  ): AuthSession {
    // Clear any existing invalid sessions first
    this.clearSessionFromStorage();

    // Use the ID token expiration as the session expiration
    const expiresAt = this.getTokenExpiration(idToken);

    const session = {
      accessToken,
      idToken,
      refreshToken,
      username,
      expiresAt,
    };

    // Set the session first
    this.currentSession = session;

    // Then notify about token update
    this.notifyTokenUpdate();

    return session;
  }

  async authenticate(credentials: CredentialsDTO): Promise<AuthResponseDTO> {
    if (!this.config) {
      throw new Error('CognitoAuthService not configured');
    }

    try {
      // First, try SRP authentication (preferred secure method)

      // Create SRP session
      const srpSession = createSrpSession(
        credentials.email.toLowerCase(),
        credentials.password,
        this.config.userPoolId,
        false // Don't hash password - we're using email as username
      );

      // Initiate SRP authentication
      const initiateAuthCommand = new InitiateAuthCommand(
        wrapInitiateAuth(srpSession, {
          AuthFlow: AuthFlowType.USER_SRP_AUTH,
          ClientId: this.config.appClientId,
          AuthParameters: {
            USERNAME: credentials.email.toLowerCase(),
          },
        })
      );

      const initiateResponse = await this.cognitoClient.send(initiateAuthCommand);

      // Handle PASSWORD_VERIFIER challenge
      if (initiateResponse.ChallengeName === 'PASSWORD_VERIFIER') {
        const signedSrpSession = signSrpSession(srpSession, initiateResponse);

        const respondCommand = new RespondToAuthChallengeCommand(
          wrapAuthChallenge(signedSrpSession, {
            ClientId: this.config.appClientId,
            ChallengeName: ChallengeNameType.PASSWORD_VERIFIER,
            Session: initiateResponse.Session, // This might be undefined, which is fine
            ChallengeResponses: {
              USERNAME: credentials.email.toLowerCase(),
            },
          })
        );

        const passwordVerifierResponse = await this.cognitoClient.send(respondCommand);

        // Handle successful authentication without additional challenges
        if (passwordVerifierResponse.AuthenticationResult) {
          const newRefreshToken = passwordVerifierResponse.AuthenticationResult.RefreshToken!;

          this.createSessionWithExpiration(
            passwordVerifierResponse.AuthenticationResult.AccessToken!,
            passwordVerifierResponse.AuthenticationResult.IdToken!,
            newRefreshToken,
            credentials.email.toLowerCase()
          );

          // Save to local storage
          this.saveSessionToStorage(this.currentSession!);

          return {
            isSignedIn: true,
            isFirstLogin: false,
          };
        }

        // Handle additional challenges after password verification
        if (passwordVerifierResponse.ChallengeName && passwordVerifierResponse.Session) {
          this.currentChallenge = {
            session: passwordVerifierResponse.Session,
            challengeName: passwordVerifierResponse.ChallengeName,
            username: credentials.email.toLowerCase(),
          };

          return this.handleChallenge(passwordVerifierResponse.ChallengeName);
        }

        // If we get here, the password verifier response was unexpected
        console.error('Unexpected password verifier response:', passwordVerifierResponse);
        throw new Error('Password verification failed - unexpected response format');
      }

      // Handle direct challenges (shouldn't happen with SRP but keeping for completeness)
      if (initiateResponse.ChallengeName && initiateResponse.Session) {
        this.currentChallenge = {
          session: initiateResponse.Session,
          challengeName: initiateResponse.ChallengeName,
          username: credentials.email.toLowerCase(),
        };

        return this.handleChallenge(initiateResponse.ChallengeName);
      }

      // Handle successful authentication without challenges (shouldn't happen with SRP)
      if (initiateResponse.AuthenticationResult) {
        this.createSessionWithExpiration(
          initiateResponse.AuthenticationResult.AccessToken!,
          initiateResponse.AuthenticationResult.IdToken!,
          initiateResponse.AuthenticationResult.RefreshToken!,
          credentials.email.toLowerCase()
        );

        // Save to local storage
        this.saveSessionToStorage(this.currentSession!);

        return {
          isSignedIn: true,
          isFirstLogin: false,
        };
      }

      // If we get here, log the full response to understand what we're missing
      console.error('Unexpected response from Cognito InitiateAuth:', initiateResponse);
      throw new Error('Unexpected response from Cognito - please check console for details');
    } catch (error: unknown) {
      const cognitoError = error as CognitoError;
      console.error('SRP Authentication error:', error);

      // If SRP fails with NotAuthorizedException, try USER_PASSWORD_AUTH as fallback
      // This handles temporary passwords set via AdminSetUserPasswordCommand
      if (cognitoError.name === 'NotAuthorizedException') {
        try {
          // Try direct password authentication (for temp passwords from AdminSetUserPasswordCommand)
          const fallbackCommand = new InitiateAuthCommand({
            AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
            ClientId: this.config!.appClientId,
            AuthParameters: {
              USERNAME: credentials.email.toLowerCase(),
              PASSWORD: credentials.password,
            },
          });

          const fallbackResponse = await this.cognitoClient.send(fallbackCommand);

          // Handle authentication result
          if (fallbackResponse.AuthenticationResult) {
            this.createSessionWithExpiration(
              fallbackResponse.AuthenticationResult.AccessToken!,
              fallbackResponse.AuthenticationResult.IdToken!,
              fallbackResponse.AuthenticationResult.RefreshToken!,
              credentials.email.toLowerCase()
            );

            // Save to local storage
            this.saveSessionToStorage(this.currentSession!);

            return {
              isSignedIn: true,
              isFirstLogin: false,
            };
          }

          // Handle challenges (most likely NEW_PASSWORD_REQUIRED for temp passwords)
          if (fallbackResponse.ChallengeName && fallbackResponse.Session) {
            this.currentChallenge = {
              session: fallbackResponse.Session,
              challengeName: fallbackResponse.ChallengeName,
              username: credentials.email.toLowerCase(),
            };

            return this.handleChallenge(fallbackResponse.ChallengeName);
          }

          // If we get here, authentication failed
          throw new Error('Authentication failed with both SRP and direct password methods');
        } catch (fallbackError: unknown) {
          console.error('USER_PASSWORD_AUTH fallback also failed:', fallbackError);

          // If both methods fail, return the original error
          return {
            isSignedIn: false,
            isFirstLogin: false,
            errorMessage: 'Invalid username or password.',
          };
        }
      }

      // Handle other errors
      let errorMessage = '';
      switch (cognitoError.name) {
        case 'NotAuthorizedException':
          errorMessage = 'Invalid username or password.';
          break;
        case 'UserNotFoundException':
          errorMessage = 'No account found with this email.';
          break;
        case 'UserNotConfirmedException':
          errorMessage =
            'Account not verified. Please check your email for verification instructions.';
          break;
        case 'TooManyRequestsException':
          errorMessage = 'Too many requests. Please try again later.';
          break;
        case 'InvalidParameterException':
          if (cognitoError.message.includes('USER_SRP_AUTH')) {
            errorMessage =
              'USER_SRP_AUTH flow is not enabled for this app. Please enable it in Cognito settings.';
          } else {
            errorMessage = 'Invalid login parameters.';
          }
          break;
        default:
          errorMessage = 'An unexpected error occurred during sign in.';
      }

      return {
        isSignedIn: false,
        isFirstLogin: false,
        errorMessage,
      };
    }
  }

  async logout(): Promise<void> {
    if (this.currentSession?.accessToken) {
      try {
        const command = new GlobalSignOutCommand({
          AccessToken: this.currentSession.accessToken,
        });
        await this.cognitoClient.send(command);
      } catch (error) {
        console.error('Logout error:', error);
        // Continue with local logout even if server logout fails
      }
    }

    this.currentSession = null;
    this.currentChallenge = null;

    // Clear session from local storage
    this.clearSessionFromStorage();
  }

  async initUser(data: RegisterUserDTO): Promise<boolean> {
    if (!this.currentChallenge || this.currentChallenge.challengeName !== 'NEW_PASSWORD_REQUIRED') {
      throw new Error('No new password challenge found');
    }

    try {
      const command = new RespondToAuthChallengeCommand({
        ClientId: this.config!.appClientId,
        ChallengeName: ChallengeNameType.NEW_PASSWORD_REQUIRED,
        Session: this.currentChallenge.session,
        ChallengeResponses: {
          USERNAME: this.currentChallenge.username,
          NEW_PASSWORD: data.newPassword,
          'userAttributes.name': `${data.firstName} ${data.lastName}`,
        },
      });

      const response = await this.cognitoClient.send(command);

      if (response.AuthenticationResult) {
        this.createSessionWithExpiration(
          response.AuthenticationResult.AccessToken!,
          response.AuthenticationResult.IdToken!,
          response.AuthenticationResult.RefreshToken!,
          this.currentChallenge.username
        );

        // Save to local storage
        this.saveSessionToStorage(this.currentSession!);

        this.currentChallenge = null;
        return true;
      }

      return false;
    } catch (error) {
      console.error('User initialization error:', error);
      throw new Error('Failed to set new password. Please try again.');
    }
  }

  async loadUser(): Promise<UserInfoDTO> {
    // Ensure we have a valid session (will refresh if needed)
    await this.ensureValidSession();

    try {
      const command = new GetUserCommand({
        AccessToken: this.currentSession!.accessToken,
      });

      const response = await this.cognitoClient.send(command);

      const attributes = response.UserAttributes || [];
      const getAttributeValue = (name: string) =>
        attributes.find((attr) => attr.Name === name)?.Value || '';

      return {
        id: getAttributeValue('sub'),
        name: getAttributeValue('name'),
        email: getAttributeValue('email'),
      };
    } catch (error) {
      console.error('Load user error:', error);
      throw new Error('Failed to load user information');
    }
  }

  async passwordReset(data: ResetPasswordRequestDTO): Promise<void> {
    if (!this.config) {
      throw new Error('CognitoAuthService not configured');
    }

    try {
      const command = new ForgotPasswordCommand({
        ClientId: this.config.appClientId,
        Username: data.email.toLowerCase(),
      });

      await this.cognitoClient.send(command);
    } catch (error: unknown) {
      const cognitoError = error as CognitoError;
      console.error('Password reset error:', error);

      if (cognitoError.name === 'UserNotFoundException') {
        throw new Error('No account found with this email address.');
      }

      throw new Error('Failed to initiate password reset. Please try again.');
    }
  }

  async passwordResetConfirm(data: ResetPasswordConfirmDTO): Promise<void> {
    if (!this.config) {
      throw new Error('CognitoAuthService not configured');
    }

    try {
      const command = new ConfirmForgotPasswordCommand({
        ClientId: this.config.appClientId,
        Username: data.email.toLowerCase(),
        ConfirmationCode: data.code,
        Password: data.password,
      });

      await this.cognitoClient.send(command);
    } catch (error: unknown) {
      const cognitoError = error as CognitoError;
      console.error('Password reset confirmation error:', error);

      switch (cognitoError.name) {
        case 'CodeMismatchException':
          throw new Error('Invalid confirmation code.');
        case 'ExpiredCodeException':
          throw new Error('Confirmation code has expired.');
        case 'InvalidPasswordException':
          throw new Error('Password does not meet requirements.');
        default:
          throw new Error('Failed to reset password. Please try again.');
      }
    }
  }

  async refreshSession(): Promise<string> {
    if (!this.currentSession?.refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const command = new InitiateAuthCommand({
        AuthFlow: AuthFlowType.REFRESH_TOKEN_AUTH,
        ClientId: this.config!.appClientId,
        AuthParameters: {
          REFRESH_TOKEN: this.currentSession.refreshToken,
        },
      });

      const response = await this.cognitoClient.send(command);

      if (response.AuthenticationResult) {
        this.createSessionWithExpiration(
          response.AuthenticationResult.AccessToken!,
          response.AuthenticationResult.IdToken!,
          // Refresh token might be updated
          response.AuthenticationResult.RefreshToken || this.currentSession.refreshToken,
          this.currentSession.username
        );

        // Update local storage with refreshed session
        this.saveSessionToStorage(this.currentSession!);

        // Notify any listeners about token update (for auth store sync)
        this.notifyTokenUpdate();

        return this.currentSession.idToken;
      }

      throw new Error('Failed to refresh session - no authentication result');
    } catch (error) {
      console.error('Session refresh error:', error);

      // Clear the invalid session
      this.clearSessionFromStorage();

      // Provide more specific error messages
      const cognitoError = error as CognitoError;
      if (cognitoError.name === 'NotAuthorizedException') {
        if (cognitoError.message?.includes('Invalid Refresh Token')) {
          throw new Error('Your session has expired. Please sign in again.');
        } else {
          throw new Error('Authentication failed. Please sign in again.');
        }
      }

      throw new Error('Failed to refresh session. Please sign in again.');
    }
  }

  async verifyMFACode(code: string): Promise<boolean> {
    if (!this.currentChallenge) {
      throw new Error('No active MFA challenge found');
    }

    try {
      const challengeResponses: Record<string, string> = {
        USERNAME: this.currentChallenge.username,
      };

      switch (this.currentChallenge.challengeName) {
        case 'SMS_MFA':
          challengeResponses.SMS_MFA_CODE = code;
          break;
        case 'SOFTWARE_TOKEN_MFA':
          challengeResponses.SOFTWARE_TOKEN_MFA_CODE = code;
          break;
        case 'EMAIL_OTP':
          challengeResponses.EMAIL_OTP_CODE = code;
          break;
        default:
          throw new Error(`Unsupported MFA challenge: ${this.currentChallenge.challengeName}`);
      }

      const command = new RespondToAuthChallengeCommand({
        ClientId: this.config!.appClientId,
        ChallengeName: this.currentChallenge.challengeName as ChallengeNameType,
        Session: this.currentChallenge.session,
        ChallengeResponses: challengeResponses,
      });

      const response = await this.cognitoClient.send(command);

      if (response.AuthenticationResult) {
        this.createSessionWithExpiration(
          response.AuthenticationResult.AccessToken!,
          response.AuthenticationResult.IdToken!,
          response.AuthenticationResult.RefreshToken!,
          this.currentChallenge.username
        );

        // Save to local storage
        this.saveSessionToStorage(this.currentSession!);

        this.currentChallenge = null;
        return true;
      }

      return false;
    } catch (error: unknown) {
      const cognitoError = error as CognitoError;
      console.error('MFA verification error:', error);

      if (cognitoError.name === 'CodeMismatchException') {
        throw new Error('Invalid MFA code. Please try again.');
      }

      throw new Error('MFA verification failed. Please try again.');
    }
  }

  async setupMFA(method: MFASelectionType): Promise<MFASetupDTO> {
    // Ensure we have a valid session (will refresh if needed)
    await this.ensureValidSession();

    try {
      switch (method) {
        case 'totp': {
          const command = new AssociateSoftwareTokenCommand({
            AccessToken: this.currentSession!.accessToken,
          });

          const response = await this.cognitoClient.send(command);

          if (!response.SecretCode) {
            throw new Error('Failed to get TOTP secret code');
          }

          const appName = 'Kunnusta.io';
          const username = this.currentSession!.username;
          const setupUri = `otpauth://totp/${appName}:${username}?secret=${response.SecretCode}&issuer=${appName}`;

          return {
            qrCode: setupUri,
            secretCode: response.SecretCode,
          };
        }

        case 'sms': {
          return {
            qrCode: '',
            secretCode: '',
            setupMessage:
              'SMS MFA setup initiated. You will receive verification codes via SMS during sign-in.',
          };
        }

        case 'email': {
          const userInfo = await this.loadUser();
          return {
            qrCode: '',
            secretCode: userInfo.email,
            setupMessage:
              'Email MFA is available. You will receive verification codes via email during sign-in.',
          };
        }

        default:
          throw new Error('Unsupported MFA method. Please use TOTP, SMS, or Email MFA.');
      }
    } catch (error) {
      console.error('MFA setup failed:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to set up MFA. Please try again.');
    }
  }

  async enableMFA(
    method: MFASelectionType,
    verificationCode: string,
    _secretKey?: string
  ): Promise<boolean> {
    // Ensure we have a valid session (will refresh if needed)
    await this.ensureValidSession();

    try {
      switch (method) {
        case 'totp': {
          if (!verificationCode || verificationCode.length !== 6) {
            throw new Error('Invalid verification code');
          }

          // Verify the TOTP code
          const verifyCommand = new VerifySoftwareTokenCommand({
            AccessToken: this.currentSession!.accessToken,
            UserCode: verificationCode,
          });

          await this.cognitoClient.send(verifyCommand);

          // Set TOTP as preferred MFA method
          const totpPreferenceCommand = new SetUserMFAPreferenceCommand({
            AccessToken: this.currentSession!.accessToken,
            SoftwareTokenMfaSettings: {
              Enabled: true,
              PreferredMfa: true,
            } as SoftwareTokenMfaSettingsType,
          });

          await this.cognitoClient.send(totpPreferenceCommand);
          return true;
        }

        case 'sms': {
          // Set SMS as preferred MFA method
          const smsPreferenceCommand = new SetUserMFAPreferenceCommand({
            AccessToken: this.currentSession!.accessToken,
            SMSMfaSettings: {
              Enabled: true,
              PreferredMfa: true,
            } as SMSMfaSettingsType,
          });

          await this.cognitoClient.send(smsPreferenceCommand);
          return true;
        }

        case 'email': {
          // Email MFA is typically configured at the user pool level
          // and doesn't require individual user preference setting
          return true;
        }

        default:
          throw new Error('Unsupported MFA method. Please use TOTP, SMS, or Email MFA.');
      }
    } catch (error) {
      console.error('Failed to enable MFA:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to enable MFA. Please check your verification code and try again.');
    }
  }

  // Public method to check if user is authenticated (with valid session)
  async isAuthenticated(): Promise<boolean> {
    try {
      if (!this.currentSession) {
        return false;
      }

      // Check if we have required tokens
      if (!this.currentSession.refreshToken || !this.currentSession.idToken) {
        return false;
      }

      // If session is expired, attempt to refresh it
      if (this.isSessionExpired(this.currentSession)) {
        try {
          // Attempt to refresh the session
          await this.refreshSession();
          return true; // Refresh successful
        } catch (refreshError) {
          // Refresh failed, session is invalid
          this.currentSession = null;
          this.clearSessionFromStorage();
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('Error checking authentication status:', error);
      return false;
    }
  }

  // Public method to check if session needs refresh (expires in <10 minutes)
  isSessionNearExpiry(): boolean {
    if (!this.currentSession) {
      return false;
    }

    const now = Date.now();
    const timeUntilExpiration = this.currentSession.expiresAt - now;
    const isNearExpiry = timeUntilExpiration <= CognitoAuthService.TOKEN_BUFFER_TIME;

    return isNearExpiry;
  }

  // Public method to get current tokens (for synchronization with auth store)
  getCurrentTokens(): {
    accessToken: string | null;
    idToken: string | null;
    refreshToken: string | null;
  } | null {
    if (!this.currentSession) {
      return null;
    }

    return {
      accessToken: this.currentSession.accessToken,
      idToken: this.currentSession.idToken,
      refreshToken: this.currentSession.refreshToken,
    };
  }

  // Method to register callback for token updates
  onTokenUpdate(
    callback: (tokens: {
      accessToken: string | null;
      idToken: string | null;
      refreshToken: string | null;
    }) => void
  ): void {
    this.tokenUpdateCallbacks.push(callback);
  }

  // Method to remove token update callback
  removeTokenUpdateCallback(
    callback: (tokens: {
      accessToken: string | null;
      idToken: string | null;
      refreshToken: string | null;
    }) => void
  ): void {
    const index = this.tokenUpdateCallbacks.indexOf(callback);
    if (index > -1) {
      this.tokenUpdateCallbacks.splice(index, 1);
    }
  }

  // Private method to notify listeners about token updates
  private notifyTokenUpdate(): void {
    const tokens = this.getCurrentTokens();
    if (tokens) {
      this.tokenUpdateCallbacks.forEach((callback) => {
        try {
          callback(tokens);
        } catch (error) {
          console.error('Error in token update callback:', error);
        }
      });
    }
  }

  private handleChallenge(challengeName: string): AuthResponseDTO {
    switch (challengeName) {
      case 'NEW_PASSWORD_REQUIRED':
        return {
          isSignedIn: false,
          isFirstLogin: true,
        };

      case 'SMS_MFA':
        return {
          isSignedIn: false,
          isFirstLogin: false,
          requiresMFA: true,
          mfaMethod: 'sms',
        };

      case 'SOFTWARE_TOKEN_MFA':
        return {
          isSignedIn: false,
          isFirstLogin: false,
          requiresMFA: true,
          mfaMethod: 'totp',
        };

      case 'EMAIL_OTP':
        return {
          isSignedIn: false,
          isFirstLogin: false,
          requiresMFA: true,
          mfaMethod: 'email',
        };

      case 'MFA_SETUP':
        return {
          isSignedIn: false,
          isFirstLogin: false,
          requiresMFA: true,
          mfaMethod: 'selection',
        };

      default:
        throw new Error(`Unsupported challenge type: ${challengeName}`);
    }
  }

  /**
   * Performs basic security checks and logs warnings about potential vulnerabilities
   */
  private performSecurityChecks(): void {
    try {
      // Check if running over HTTPS (except localhost)
      if (
        typeof window !== 'undefined' &&
        window.location.protocol === 'http:' &&
        !window.location.hostname.includes('localhost') &&
        !window.location.hostname.includes('127.0.0.1')
      ) {
        // Production security check - keep this warning
        console.warn(
          '🔒 SECURITY WARNING: Authentication tokens transmitted over HTTP. Use HTTPS in production.'
        );
      }
    } catch (error) {
      // Silently handle security check errors
    }
  }
}
